/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import { useTranslation } from 'react-i18next'
import { CompanyAddress } from 'types/entities/CompanyAddress'
import { getLocalizedCountryName } from 'utils/localizedCountryUtils'
import { formatAddressSummary } from 'utils/addressUtils'

interface MergeCompaniesAddressesListProps {
    addresses?: CompanyAddress[]
    emptyLabel: string
}

const MergeCompaniesAddressesList = ({
    addresses,
    emptyLabel,
}: MergeCompaniesAddressesListProps) => {
    const { i18n } = useTranslation()

    if (!addresses || addresses.length === 0) {
        return (
            <div className="text-xs text-gray-400 italic bg-gray-100/50 dark:bg-gray-800/50 p-4 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 text-center">
                {emptyLabel}
            </div>
        )
    }

    return (
        <div className="MergeCompaniesAddressesList">
            {addresses.map((addr) => (
                <div
                    key={addr.id}
                    className="bg-white dark:bg-gray-900 p-3 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm"
                >
                    <span className="font-bold text-gray-900 dark:text-white text-sm block">
                        {addr.name}
                    </span>
                    <span className="text-[11px] text-gray-500 mt-0.5 line-clamp-1">
                        {[
                            formatAddressSummary(addr.street_name, addr.postal_code, addr.town),
                            getLocalizedCountryName(addr.country, i18n.language),
                        ]
                            .filter(Boolean)
                            .join(', ')}
                    </span>
                </div>
            ))}
        </div>
    )
}

export default MergeCompaniesAddressesList
